import React, { createContext, useContext, useReducer } from 'react';
import type { ReactNode } from 'react';

export const MB_SCREENS = {
    CHECKLIST_INFO: "CHECKLIST_INFO",
    CYCLE_ENTRY: "CYCLE_ENTRY",
    SUMMARY: "SUMMARY"
} as const;

export type MB_SCREENS = typeof MB_SCREENS[keyof typeof MB_SCREENS];

export interface MBSessionData {
    site: string;
    line: string;
    shift: string;
    qaExecutive: string;
    productionIncharge: string;
    productName: string;
    executiveName: string;
    batchNo: string;
    cycle: string;
    typeOfMixer: "Vertical" | "Horizontal" | string;
    tourStartDate: string;
}

export interface IngredientRow {
    standard: string;
    observed: string;
    remarks: string;
    actionTaken: string;
}

export interface RawMaterialData {
    chocoChipsSupplier: string;
    chocoChipsTemp: string;
    chocoChipsCountPerKg: string;
    chocoChipsMfgDate: string;
    chocoChipsCompoundOrPure: "Compound" | "Pure" | "";
    cashewSupplier: string;
    cashewTemp: string;
    cashewCountPerKg: string;
    cashewMfgDate: string;
    cashewCompoundOrPure: "Compound" | "Pure" | "";
    invertSyrupTemp: string;
    invertSyrupPh: string;
    invertSyrupBrix: string;
    blackjack2Temp: string;
    blackjack2Ph: string;
    blackjack2Brix: string;
    flourSupplier: string;
}

export interface MinorIngredientsChecklist {
    cocoaPowder: "Done" | "Not Done" | "";
    smp: "Done" | "Not Done" | "";
    salt1: "Done" | "Not Done" | "";
    abc: "Done" | "Not Done" | "";
    sbc: "Done" | "Not Done" | "";
    salt2: "Done" | "Not Done" | "";
    others: "Done" | "Not Done" | "";
}

export interface MixingDoughData {
    creamingTimeStd: string;
    creamingTimeObs: string;
    mixingTimeStd: string;
    mixingTimeObs: string;
    doughTempStd: string;
    doughTempObs: string;
    jacketTempStd: string;
    jacketTempObs: string;
    doughConsistencyStd: string;
    doughConsistencyObs: string;
    doughStandingTimeStd: string;
    doughStandingTimeObs: string;
}

export interface MixingSpongeData {
    spongeProductName: string;
    spongeWaterQuantity: string;
    spongeYeastQuantity: string;
    spongeWaterTemp: string;
    spongeMixingTime: string;
    fermentationStartTemp: string;
    fermentationRoomTemp: string;
    finalTempAfterFermentation: string;
    finalPhAfterFermentation: string;
}

export interface FormingData {
    typeofMixer: string;
    moulderRpmStrokes: string;
    formingSampleCount: string;
    standardWetWeight: string;
    observedWetWeight: string;
    weightBeforeSugarSprinkling: string;
    weightAfterSugarSprinkling: string;
}

export interface BakingZonesData {
    bakingTime: string;
    topZone1: string;
    topZone2: string;
    topZone3: string;
    topZone4: string;
    topZone5: string;
    topZone6: string;
    topZone7: string;
    topProductTemp: string;
    bottomZone1: string;
    bottomZone2: string;
    bottomZone3: string;
    bottomZone4: string;
    bottomZone5: string;
    bottomZone6: string;
    bottomZone7: string;
    bottomProductTemp: string;
    asPerTemplate: "Yes" | "No" | "Done" | "Not Done" | "";
    taste: string;
}

export interface StandardsColourFinishingMoisture {
    biscuitLength: string;
    biscuitWidth: string;
    biscuitDiameter: string;
    standardsSampleCount: string;
    biscuitStdWeight: string;
    biscuitObservedWeight: string;
    topColourStd: string;
    topColourObs: string;
    bottomColourStd: string;
    bottomColourObs: string;
    weightAfterOilSpray: string;
    moistureStd: string;
    moistureObs: string;
}

export interface CycleAttachment {
    id: string;
    fileName: string;
    fileSize: number;
    fileType: string;
    previewUrl: string;
    base64Data?: string;
    serverRelativeUrl?: string;
    timestamp: number;
}

export interface MBCycleRecord {
    id?: string;
    tourId?: string;
    cycle: string;
    cycleTime: string;
    batchNo: string;
    productName: string;
    executiveName: string;
    shift: string;
    lineNo: string;
    observedBy: string;
    productionIncharge: string;

    // Ingredients
    rpo: IngredientRow;
    solidFat: IngredientRow;
    butter: IngredientRow;
    blackjackInitial: IngredientRow;
    sponge: IngredientRow;
    slurry: IngredientRow;
    groundSugarTemp: IngredientRow;
    groundSugarParticleSize: IngredientRow;

    // Raw Materials & Checklist
    rawMaterials: RawMaterialData;
    minorIngredients: MinorIngredientsChecklist;

    // Mixing Dough & Sponge
    mixingDough: MixingDoughData;
    mixingSponge: MixingSpongeData;

    // Forming & Baking
    forming: FormingData;
    baking: BakingZonesData;

    // Standards & Finish
    standards: StandardsColourFinishingMoisture;

    // Attachments
    attachments?: CycleAttachment[];
    status?: "In Progress" | "Completed" | "Submitted";
}

export const createDefaultCycleRecord = (cycleName = "Cycle-1", session?: Partial<MBSessionData>): MBCycleRecord => ({
    cycle: cycleName,
    cycleTime: new Date().toISOString(),
    batchNo: session?.batchNo || "",
    productName: session?.productName || "",
    executiveName: session?.executiveName || "",
    shift: session?.shift || "Shift 1",
    lineNo: session?.line || "Line-1",
    observedBy: session?.qaExecutive || "",
    productionIncharge: session?.productionIncharge || "",
    rpo: { standard: "", observed: "", remarks: "", actionTaken: "" },
    solidFat: { standard: "", observed: "", remarks: "", actionTaken: "" },
    butter: { standard: "", observed: "", remarks: "", actionTaken: "" },
    blackjackInitial: { standard: "", observed: "", remarks: "", actionTaken: "" },
    sponge: { standard: "", observed: "", remarks: "", actionTaken: "" },
    slurry: { standard: "", observed: "", remarks: "", actionTaken: "" },
    groundSugarTemp: { standard: "", observed: "", remarks: "", actionTaken: "" },
    groundSugarParticleSize: { standard: "", observed: "", remarks: "", actionTaken: "" },
    rawMaterials: {
        chocoChipsSupplier: "",
        chocoChipsTemp: "",
        chocoChipsCountPerKg: "",
        chocoChipsMfgDate: "",
        chocoChipsCompoundOrPure: "",
        cashewSupplier: "",
        cashewTemp: "",
        cashewCountPerKg: "",
        cashewMfgDate: "",
        cashewCompoundOrPure: "",
        invertSyrupTemp: "",
        invertSyrupPh: "",
        invertSyrupBrix: "",
        blackjack2Temp: "",
        blackjack2Ph: "",
        blackjack2Brix: "",
        flourSupplier: ""
    },
    minorIngredients: {
        cocoaPowder: "",
        smp: "",
        salt1: "",
        abc: "",
        sbc: "",
        salt2: "",
        others: ""
    },
    mixingDough: {
        creamingTimeStd: "",
        creamingTimeObs: "",
        mixingTimeStd: "",
        mixingTimeObs: "",
        doughTempStd: "",
        doughTempObs: "",
        jacketTempStd: "",
        jacketTempObs: "",
        doughConsistencyStd: "",
        doughConsistencyObs: "",
        doughStandingTimeStd: "",
        doughStandingTimeObs: ""
    },
    mixingSponge: {
        spongeProductName: "",
        spongeWaterQuantity: "",
        spongeYeastQuantity: "",
        spongeWaterTemp: "",
        spongeMixingTime: "",
        fermentationStartTemp: "",
        fermentationRoomTemp: "",
        finalTempAfterFermentation: "",
        finalPhAfterFermentation: ""
    },
    forming: {
        typeofMixer: session?.typeOfMixer || "Vertical",
        moulderRpmStrokes: "",
        formingSampleCount: "10",
        standardWetWeight: "",
        observedWetWeight: "",
        weightBeforeSugarSprinkling: "",
        weightAfterSugarSprinkling: ""
    },
    baking: {
        bakingTime: "",
        topZone1: "",
        topZone2: "",
        topZone3: "",
        topZone4: "",
        topZone5: "",
        topZone6: "",
        topZone7: "",
        topProductTemp: "",
        bottomZone1: "",
        bottomZone2: "",
        bottomZone3: "",
        bottomZone4: "",
        bottomZone5: "",
        bottomZone6: "",
        bottomZone7: "",
        bottomProductTemp: "",
        asPerTemplate: "Yes",
        taste: "Normal / Crisp"
    },
    standards: {
        biscuitLength: "",
        biscuitWidth: "",
        biscuitDiameter: "",
        standardsSampleCount: "10",
        biscuitStdWeight: "",
        biscuitObservedWeight: "",
        topColourStd: "Golden Brown",
        topColourObs: "Golden Brown",
        bottomColourStd: "Even Brown",
        bottomColourObs: "Even Brown",
        weightAfterOilSpray: "",
        moistureStd: "2.5",
        moistureObs: ""
    },
    attachments: [],
    status: "In Progress"
});

interface MBState {
    currentScreen: MB_SCREENS;
    currentTourId: string | null;
    sessionData: MBSessionData | null;
    currentCycle: string;
    activeCycleRecord: MBCycleRecord;
    completedCycles: MBCycleRecord[];
    isLoading: boolean;
    isPaused: boolean;
}

type MBAction =
    | { type: 'SET_SCREEN'; payload: MB_SCREENS }
    | { type: 'SET_TOUR_ID'; payload: string }
    | { type: 'SET_SESSION_DATA'; payload: MBSessionData }
    | { type: 'SET_CURRENT_CYCLE'; payload: string }
    | { type: 'SET_ACTIVE_CYCLE_RECORD'; payload: MBCycleRecord }
    | { type: 'UPDATE_ACTIVE_CYCLE_RECORD'; payload: Partial<MBCycleRecord> }
    | { type: 'SET_COMPLETED_CYCLES'; payload: MBCycleRecord[] }
    | { type: 'ADD_COMPLETED_CYCLE'; payload: MBCycleRecord }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_IS_PAUSED'; payload: boolean }
    | { type: 'RESET_STATE' };

const initialSession: MBSessionData = {
    site: "Rajpura",
    line: "Line-1",
    shift: "Shift 1",
    qaExecutive: "",
    productionIncharge: "",
    productName: "Butter Bake",
    executiveName: "",
    batchNo: "",
    cycle: "Cycle-1",
    typeOfMixer: "Vertical",
    tourStartDate: new Date().toISOString()
};

const initialState: MBState = {
    currentScreen: MB_SCREENS.CHECKLIST_INFO,
    currentTourId: null,
    sessionData: initialSession,
    currentCycle: "Cycle-1",
    activeCycleRecord: createDefaultCycleRecord("Cycle-1", initialSession),
    completedCycles: [],
    isLoading: false,
    isPaused: false
};

function mbReducer(state: MBState, action: MBAction): MBState {
    switch (action.type) {
        case 'SET_SCREEN':
            return { ...state, currentScreen: action.payload };
        case 'SET_TOUR_ID':
            return { ...state, currentTourId: action.payload };
        case 'SET_SESSION_DATA': {
            const updatedActive = {
                ...state.activeCycleRecord,
                lineNo: action.payload.line,
                shift: action.payload.shift,
                productName: action.payload.productName,
                executiveName: action.payload.executiveName,
                batchNo: action.payload.batchNo,
                observedBy: action.payload.qaExecutive,
                productionIncharge: action.payload.productionIncharge,
                forming: {
                    ...state.activeCycleRecord.forming,
                    typeofMixer: action.payload.typeOfMixer || state.activeCycleRecord.forming.typeofMixer
                }
            };
            return {
                ...state,
                sessionData: action.payload,
                activeCycleRecord: updatedActive
            };
        }
        case 'SET_CURRENT_CYCLE': {
            return {
                ...state,
                currentCycle: action.payload,
                activeCycleRecord: createDefaultCycleRecord(action.payload, state.sessionData || undefined)
            };
        }
        case 'SET_ACTIVE_CYCLE_RECORD':
            return { ...state, activeCycleRecord: action.payload };
        case 'UPDATE_ACTIVE_CYCLE_RECORD':
            return { ...state, activeCycleRecord: { ...state.activeCycleRecord, ...action.payload } };
        case 'SET_COMPLETED_CYCLES':
            return { ...state, completedCycles: action.payload };
        case 'ADD_COMPLETED_CYCLE': {
            const filtered = state.completedCycles.filter(c => c.cycle !== action.payload.cycle);
            return { ...state, completedCycles: [...filtered, action.payload] };
        }
        case 'SET_LOADING':
            return { ...state, isLoading: action.payload };
        case 'SET_IS_PAUSED':
            return { ...state, isPaused: action.payload };
        case 'RESET_STATE':
            return initialState;
        default:
            return state;
    }
}

const MixingAndBakingContext = createContext<{
    state: MBState;
    dispatch: React.Dispatch<MBAction>;
}>({
    state: initialState,
    dispatch: () => null
});

export const MixingAndBakingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(mbReducer, initialState);

    return (
        <MixingAndBakingContext.Provider value={{ state, dispatch }}>
            {children}
        </MixingAndBakingContext.Provider>
    );
};

export const useMixingAndBakingContext = () => useContext(MixingAndBakingContext);
